import React, { useEffect, useMemo, useState, useRef, KeyboardEvent, ChangeEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { showSuccess, showError } from "toast/toast";
import { Row } from "@tanstack/react-table";
import DataTable from "components/DataTable";
import PaginationControls from "components/PaginationControls";
import TablePaginationControls from "components/TableandmobilePaginationcontrol"
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
import {
  DesktopTableLoader,
  MobileCardsLoader,
  DataTableStatus,
} from "components/DataTableLoader";
import { ViewIconButton, EditIconButton, DeleteIconButton } from "components/DatatablesActionIcons";
import DeleteConfirmModal from "components/ToolTips/DeleteConfirmModal";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";

import {
  ExportAsExcel,
} from "@siamf/react-export";

import { Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { getAuthCookie } from "../../utils/authCookies";
import { useModuleActionPermissions } from "hooks/useModuleActionPermissions";
import { MODULE_PERMISSION_NAMES } from "utils/moduleActionPermissions";

type Attribute = {
  id: string;
  name: string;
  values: string[];
  description: string;
  status: string;
};

function fuzzyTextFilter<TData>(
  row: Row<TData>,
  columnId: string,
  filterValue: string
) {
  const raw = row.getValue(columnId);
  const value = (typeof raw === "boolean"
    ? raw
      ? "Active"
      : "Inactive"
    : String(raw)
  ).toLowerCase();

  return value.includes(filterValue.toLowerCase());
}

const columnHelper = createColumnHelper<Attribute>();

const LIST_TEXT_MAX_LEN = 40;

function truncateListText(text: string, maxLen = LIST_TEXT_MAX_LEN): { display: string; full: string } {
  const full = (text || "").trim();
  if (!full) return { display: "", full: "" };
  if (full.length <= maxLen) return { display: full, full };
  return { display: `${full.slice(0, maxLen)}…`, full };
}

const SignageAttributes = () => {
  const [attributes, setAttributes] = useState<Attribute[]>([]);
  const [filteredAttributes, setFilteredAttributes] = useState<Attribute[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [error, setError] = useState<string>("");
  const token = getAuthCookie("sessionToken");
  const navigate = useNavigate();
  const location = useLocation();

  // Determine the base route based on current location
  const isSignageTeammember = location.pathname.includes('/signageteammember');
  const baseRoute = isSignageTeammember ? '/signageteammember' : '/signage';
  const { canCreate, canView, canEdit, canDelete } = useModuleActionPermissions(
    MODULE_PERMISSION_NAMES.attributes
  );

  const [rowCount, setRowCount] = useState(0);
  const itemsPerPage = 10;
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [globalFilter, setGlobalFilter] = useState("");
  const [filteredAgents, setFilteredAgents] = useState<Attribute[]>([]);
  const [searchInput, setSearchInput] = useState<string>(globalFilter ?? "");
  const [statusFilterValue, setStatusFilterValue] = useState("");
  const topRef = useRef<HTMLDivElement>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [exportRows, setExportRows] = useState<any[]>([]);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);


  useEffect(() => {
    const fetchAttributes = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/product-attributes/datatable`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              page: pagination.pageIndex + 1,
              limit: pagination.pageSize,
              search: globalFilter,
              filters: statusFilterValue ? { status: statusFilterValue } : {},
              sort: {
                field: "created_at",
                order: "desc",
              },
            }),
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch attributes");
        }

        const result = await response.json();
        setRowCount(result.total || 0);

        const attributeData = result?.data ?? [];
        // console.log("attributeData", result);
        
        if (Array.isArray(attributeData)) {
          setAttributes(attributeData);
          setFilteredAgents(attributeData);
        } else {
          throw new Error("Unexpected response format for attributes");
        }
      } catch (err) {
        showError("Failed to load attributes.");
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchAttributes();
  }, [token, globalFilter, pagination, deleting, statusFilterValue]);

  useEffect(() => {
    const filtered = attributes?.filter((attr) =>
      attr.name.toLowerCase().includes(searchInput.toLowerCase())
    );
    setFilteredAttributes(filtered);
  }, [searchInput, attributes]);

  const handleDeleteClick = (attribute: Attribute) => {
    setDeleteTarget({ id: attribute.id, name: attribute.name || "Attribute" });
  };

  const handleDeleteCancel = () => setDeleteTarget(null);

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    await deleteAttribute(deleteTarget.id);
    setDeleteTarget(null);
  };

  const deleteAttribute = async (attributeId: string) => {
    setDeleting(attributeId);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/product-attributes/${attributeId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const result: any = await response.json();
      if (!response.ok || result?.error) {
        const message = result?.error || result?.message || "Failed to save data";
        console.error("API Error:", message);
        showError(message);
        throw new Error(message);
      }

      showSuccess("Attribute deleted successfully!");
      setAttributes((prev) => prev?.filter((attr) => attr.id !== attributeId));
      setDeleting(null);
      // navigate(0);
    } catch (err) {
      showError("Failed to delete the attribute.");
      setDeleting(null);
    }
  };
  const columns = useMemo(() => {
    return [
      columnHelper.display({
        id: "serial",
        header: () => (
          <div className="th-text text-center S-No">S.No.</div>
        ),
        cell: (info) => (
          <div className="text-sm text-center S-No">
            {pagination.pageIndex * pagination.pageSize + info.row.index + 1}
          </div>
        ),
      }),
      columnHelper.accessor(
        (row: Attribute) => `${row?.name}`,
        {
          id: "name",
          header: () => (
            <div className="th-text text-center S-No-2">Name</div>
          ),
          cell: (info) => (
            <div className="text-sm text-center S-No-2">{info.getValue()}</div>
          ),
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }
      ),
      columnHelper.accessor(
        (row: Attribute) => row.values.join(", "),
        {
          id: "values",
          header: () => (
            <div className="th-text text-center">Values</div>
          ),
          cell: (info) => {
            const { display, full } = truncateListText(String(info.getValue() ?? ""));
            return (
              <div className="text-sm text-center" title={full || undefined}>
                {display || <span className="text-muted">—</span>}
              </div>
            );
          },
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }
      ),
      columnHelper.accessor("status", {
        header: () => (
          <div className="th-text text-center">Status</div>
        ),
        cell: (info) => {
          const value = info.getValue() as string | boolean;
          const isActive = value === true || value === "true";

          const className = isActive
            ? "status-radio status-radio-active"
            : "status-radio status-radio-inactive";

          return (
            <div className="text-sm text-center">
              <input
                type="radio"
                className={className}
                checked={isActive}
                readOnly
              />
            </div>
          );
        },
        enableGlobalFilter: true,
      }),
      columnHelper.display({
        id: "actions",
        header: () => (
          <div className="th-text text-center">Actions</div>
        ),
        cell: (info) => {
          const attributeInfo = info.row.original;
          if (!canView && !canEdit && !canDelete) return null;
          return (
            <div className="d-flex align-items-center justify-content-center gap-2">
              {canEdit && (
                <EditIconButton
                  to={`${baseRoute}/attribute/${attributeInfo.id}`}
                  className="icon-square"
                  title="Edit"
                />
              )}
              {canDelete && (
                <DeleteIconButton
                  onClick={() => handleDeleteClick(attributeInfo)}
                  className="icon-square danger"
                  title="Delete"
                  disabled={deleting === attributeInfo.id}
                />
              )}
            </div>
          );
        },
      }),
    ];
  }, [deleting, pagination, canView, canEdit, canDelete, baseRoute]);


  const table = useReactTable({
    data: attributes,
    columns,
    pageCount: Math.ceil(rowCount / pagination.pageSize),
    state: {
      pagination,
      globalFilter,
    },
    manualPagination: true,
    manualFiltering: true,
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
  });

  console.log(searchInput, "searchInput");

  const pageCount = table.getPageCount();
  const currentPage = table.getState().pagination.pageIndex;

  // Scroll to top when page changes
  useEffect(() => {
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [pagination.pageIndex]);

  // Prepare export data
  useEffect(() => {
    const rows = attributes.map((attr, index) => {
      const isActive = String(attr.status).toLowerCase() === "true";
      return {
        "S.No.": pagination.pageIndex * pagination.pageSize + index + 1,
        "Name": attr.name || '',
        "Values": attr.values?.join(", ") || '',
        "Description": attr.description || '',
        "Status": isActive ? 'Active' : 'Inactive',
      };
    });
    setExportRows(rows);
  }, [attributes, pagination]);

  return (
    <div className="content container" ref={topRef}>
      {/* <div className="d-flex justify-content-between align-items-center pb-3 mb-2 py-2">
        <h4 className="mb-0 "></h4>
        <div className="d-flex gap-3 align-items-center">
          <Link to="/signage/addAttribute" className="btn btn-primary  table-addbutton">
            + Add Attributes
          </Link>
        </div>
      </div> */}
      <div className="po-header mb-4 shadow-border-datatables ">
        <div className="po-top d-flex justify-content-between align-items-start align-items-md-center flex-wrap gap-2">
          <div className="po-title-wrap me-2 d-flex align-items-center">
            <h4 className="po-title mb-0">Attributes</h4>
          </div>

          {/* tools block (search + buttons) */}
          <div className="po-tools d-flex align-items-center flex-wrap gap-2 ms-auto">
            {/* Search */}
            <div className="input-group po-search ">
              <span className="input-group-text">
                <i className="ri-search-line" />
              </span>
              <input
                type="text"
                id="searchInput"
                name="searchInput"
                className="form-control"
                placeholder="Search by Name"
                value={searchInput}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === "Enter") {
                    setGlobalFilter(searchInput);
                    setPagination((prev) => ({ ...prev, pageIndex: 0 })); // ✅ Reset to page 1
                  }
                }}
                autoComplete="off"
              />
            </div>

            {/* Filters toggle */}
            {/* <button
              type="button"
              className={` po-filter-btn d-inline-flex align-items-center ${filtersOpen ? "active" : ""}`}
              onClick={() => setFiltersOpen(v => !v)}
              aria-expanded={filtersOpen}
              aria-controls="attribute-filters"
            >
              <i className="ri-filter-3-line me-2" />
              <span>Filters</span>
              <i className={`ri-arrow-down-s-line ms-1 ${filtersOpen ? "rotate-180" : ""}`} />
            </button>

            <ExportAsExcel
              data={exportRows}
              headers={[
                "S.No.",
                "Name",
                "Values",
                "Description",
                "Status",
              ]}
              fileName="Attributes"
            >
              {(props) => (
                <button {...props} className="export-button">
                  <i className="ri-file-excel-2-line me-2" />
                  Export to Excel
                </button>
              )}
            </ExportAsExcel> */}
            
            {canCreate && (
              <Link to={`${baseRoute}/attribute`} className="btn btn-primary  table-addbutton">
                + Add Attributes
              </Link>
            )}
          </div>
        </div>

        {/* Filters Section */}
        <div id="attribute-filters" className={`po-filters ${filtersOpen ? "show" : ""}`}>
          <div className="po-filters-bar d-flex">
            <div className="row g-2 g-md-3 align-items-end justify-content-md-end ms-md-auto w-100 w-md-auto">
              <div className="col-md-4">
                <label className="form-label">Status</label>
                <select
                  className="form-select"
                  value={statusFilterValue}
                  onChange={(e) => {
                    setStatusFilterValue(e.target.value);
                    setPagination((prev) => ({ ...prev, pageIndex: 0 })); // Reset to page 1
                  }}
                >
                  <option value="">All Status</option>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="card overflow-hidden production-card box-shadow-datatables shadow-border-datatables">
        {/* Header like screenshot (left title, right count pill) */}
        <div className="align-items-center bg-white border-bottom  d-flex justify-content-between px-4 py-4">
          <h5 className="mb-0 "></h5>
          {/* <span className="badge rounded-pill bg-body-tertiary text-secondary-emphasis px-3 py-2">
                  orders
                </span> */}
        </div>
        <hr className="mb-2" style={{ borderTop: "none" }} />
        {/* DESKTOP (≥ 1022px) */}
        <div className="rdt-desktop ">
          <div
            className="production-scroll"
            style={{ "--rdt-min": "1200px" } as React.CSSProperties}
          >
            <table className="table production-table align-middle mb-0 table-1">
              {loading ? (
                <DesktopTableLoader table={table} message="Loading attributes…" />
              ) : (
                <DesktopTableParts table={table} />
              )}
            </table>
          </div>
        </div>

        {/* MOBILE (≤ 1021px) */}
        <div className="rdt-mobile">
          {loading ? (
            <MobileCardsLoader message="Loading attributes…" />
          ) : (
            <MobileCardsOnly
            table={table}
            idColId="order_link"
            titleColId="product_name"
            actionColId="actions"
          />
          )}
        </div>

      </div>
      <DataTableStatus
        loading={loading}
        isEmpty={table.getRowModel().rows.length === 0}
      >
        <div className="mt-4 mb-4 ">
          <TablePaginationControls table={table} />
        </div>
      </DataTableStatus>
      <DeleteConfirmModal
        open={!!deleteTarget}
        dynamicName={deleteTarget?.name || "Attribute"}
        onCancel={handleDeleteCancel}
        onDelete={handleDeleteConfirm}
      />
    </div>
  );
};

export default SignageAttributes;
